import { EntityOnProjectRoute } from 'components/common/withProjectChangeDialog/model';
import { useUploadProgressStore } from 'store/uploadProgress';
import { DialogFactory } from 'components/core/Dialog/common/DialogContext';
import { getProjectStoreState } from 'store/project';
import { ReactElement } from 'react';
import { FormattedMessage, unsafeTranslationKey } from 'intl';
import { DialogTranslationKeys } from 'intl/keys';
import { translationKeyToString } from 'intl/types/translationKey';
import { showErrorToastWithMessage } from 'services/toaster';
import { CommonTranslationKeys, NotificationTranslationKeys } from 'intl/keys';

const descriptionStyle = { margin: '8px 24px' };
const pStyle = { marginBottom: 0 };

export function selectionConfirmationDialog(
  dialogFactory: DialogFactory,
  projectId: string
): Promise<void> {
  return dialogFactory({
    title: <FormattedMessage id={DialogTranslationKeys.changeProjectTitleSelect} />,
    description: (
      <div style={descriptionStyle}>
        <p style={pStyle}>
          <FormattedMessage id={DialogTranslationKeys.changeProjectMessageData} />
        </p>
      </div>
    ),
    customControllerLabels: [
      translationKeyToString(CommonTranslationKeys.cancel),
      translationKeyToString(CommonTranslationKeys.select),
    ],
    fullWidth: true,
    catchOnCancel: true,
    rejectOnOffline: true,
  })
    .then(() => {
      void getProjectStoreState().actions.selectProject(projectId);
    })
    .catch((e) => {
      if (e?.message === 'offline') {
        showErrorToastWithMessage(NotificationTranslationKeys.noInternetConnection);
      }
    });
}

export function changeConfirmationDialog(
  dialogFactory: DialogFactory,
  projectId: string,
  projectName?: string,
  entity?: EntityOnProjectRoute
): Promise<void> {
  return dialogFactory({
    title: <FormattedMessage id={DialogTranslationKeys.changeProjectTitleChange} />,
    description: (
      <div style={descriptionStyle}>
        <EntityInAnotherProjectMessage
          projectName={projectName}
          entity={entity}
        />
        <p style={pStyle}>
          <FormattedMessage id={DialogTranslationKeys.changeProjectMessageData} />
        </p>
        <FilesPendingMessage />
      </div>
    ),
    customControllerLabels: [
      translationKeyToString(CommonTranslationKeys.cancel),
      translationKeyToString(CommonTranslationKeys.change),
    ],
    fullWidth: true,
    catchOnCancel: true,
    rejectOnOffline: true,
  })
    .then(() => {
      void getProjectStoreState().actions.selectProject(projectId);
    })
    .catch((e) => {
      if (e?.message === 'offline') {
        showErrorToastWithMessage(NotificationTranslationKeys.noInternetConnection);
      }
    });
}

function EntityInAnotherProjectMessage({
  projectName,
  entity,
}: {
  projectName?: string;
  entity?: EntityOnProjectRoute;
}): ReactElement | null {
  if (!entity || !projectName) {
    return null;
  }

  return (
    <FormattedMessage
      id={unsafeTranslationKey(`dialog_change_project_message_${entity}`)}
      values={{ projectName }}
    />
  );
}

function FilesPendingMessage(): ReactElement | null {
  const files = useUploadProgressStore.use.files();
  const count: number = files.reduce<number>(
    (inProgressCount: number, file) =>
      file.status === 'PENDING' ? ++inProgressCount : inProgressCount,
    0
  );

  if (!count) return null;

  return (
    <p>
      <FormattedMessage
        id={DialogTranslationKeys.changeProjectMessageFiles}
        values={{ count }}
      />
    </p>
  );
}
